import { useState, useRef, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import { useStep } from '../hooks/useStep'
import { useToken } from '../hooks/useToken'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useAddOrg, useUpdateOrg, useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { DuplicateOrgNameError } from '@/api/orgsAPI'
import { useValidateCharity } from '../hooks/useValidateCharity'
import { useCheckDuplicate } from '../hooks/useCheckDuplicate'
import { useRequestJoin } from '../hooks/useRequestJoin'
import { useSwitchToOrg } from '../hooks/useSwitchToOrg'
import {
  stepHeadingSx,
  stepDescriptionSx,
  formLabelSx,
  inputSx,
  textFieldSx,
} from '../styles'
import { COLORS } from '../theme'
import {
  DuplicateWarning,
  INITIAL_DUPLICATE,
  type DuplicateState,
} from '../components/DuplicateWarning'
import type { CharityDetails } from '@/api/charitiesAPI'
import type { OrgFormData } from '../types'
import type { UUID } from 'crypto'

const EMPTY_ORG_FORM: OrgFormData = {
  orgName: '',
  website: '',
  description: '',
  regionIds: [],
  iwi: [],
  showIwiInput: false,
  refuseGambling: false,
  acceptInternational: false,
}

function getLabels(entityType: string | null) {
  if (entityType === 'other') {
    return {
      heading: 'Organisation name',
      placeholder: 'e.g. My Community Group',
    }
  }
  return { heading: 'Charity registration number', placeholder: 'e.g. CC12345' }
}

export default function OrgNumber() {
  const { orgUuid, setOrgUuid } = useOnboardingV2()
  const [wizardData, setWizardData] = useWizardData()
  const { getToken } = useToken()
  const { user, token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''
  const addOrg = useAddOrg()
  const updateOrg = useUpdateOrg()
  const { orgData } = useCurrentUserOrgs(token, user, orgUuid ? [orgUuid as UUID] : [])
  const existingOrg = orgData.find(o => o.uuid === orgUuid) ?? null
  const validateCharity = useValidateCharity()
  const checkDuplicate = useCheckDuplicate()
  const requestJoinMutation = useRequestJoin()
  const switchToOrg = useSwitchToOrg()
  const [, setSearchParams] = useSearchParams()

  const isCharity = wizardData.entityType === 'charity'

  // For charities: orgNumber holds the CC number
  // For other entities: orgForm.orgName holds the org name (orgNumber stays empty)
  const fieldValue = isCharity
    ? wizardData.orgNumber
    : (wizardData.orgForm?.orgName ?? '')

  const [charityDetails, setCharityDetails] = useState<CharityDetails | null>(
    wizardData.charityDetails,
  )
  const [validationError, setValidationError] = useState('')
  const [validating, setValidating] = useState(false)
  const [duplicate, setDuplicate] = useState<DuplicateState>(INITIAL_DUPLICATE)
  const [requesting, setRequesting] = useState(false)
  const [switchingOrg, setSwitchingOrg] = useState(false)
  const [goToOrgError, setGoToOrgError] = useState<string | null>(null)
  const [createError, setCreateError] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The debounce below is only cleared when a new keystroke replaces it, and its
  // callback awaits network work before setting state. Without both guards an
  // in-flight validation resolves after unmount and sets state on a dead
  // component — in jsdom that surfaces as "window is not defined" once the test
  // environment has been torn down.
  const mountedRef = useRef(true)
  useEffect(() => {
    return () => {
      mountedRef.current = false
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  // A returning user with cleared localStorage has no wizard data, but the org
  // record still holds what they entered. Write the derived values back instead of
  // reading them locally — step 5 keys its charities_number write off entityType.
  // Only while entityType is unset: once it is set, an empty field means the user
  // just switched type and OrgEntityType cleared the old value on purpose.
  const restoredRef = useRef(false)
  useEffect(() => {
    if (restoredRef.current) return
    if (wizardData.entityType !== null || !existingOrg) return
    restoredRef.current = true
    const charitiesNumber = existingOrg.charities_number?.trim() ?? ''
    if (charitiesNumber) {
      setWizardData({ entityType: 'charity', orgNumber: charitiesNumber })
    } else {
      setWizardData((prev) => ({
        entityType: 'other',
        orgForm: { ...(prev.orgForm ?? EMPTY_ORG_FORM), orgName: existingOrg.name },
      }))
    }
  }, [existingOrg, wizardData.entityType, setWizardData])

  const { heading, placeholder } = getLabels(wizardData.entityType)

  // Field is disabled after request-to-join is sent
  const fieldDisabled = duplicate.requestSent

  const canContinue =
    fieldValue.trim().length > 0 &&
    !validationError &&
    !validating &&
    !fieldDisabled &&
    (!duplicate.isDuplicate || duplicate.hasSubOrgs)

  useStep({
    isValid: canContinue,
    beforeNext: async () => {
      // For "other" entities: run name-based duplicate check on advance
      if (wizardData.entityType === 'other') {
        try {
          const freshToken = await getToken()
          const result = await checkDuplicate.mutateAsync({
            token: freshToken,
            params: { name: fieldValue.trim() },
          })
          if (result.exists && !result.has_sub_orgs) {
            setDuplicate({
              ...INITIAL_DUPLICATE,
              isDuplicate: true,
              orgName: result.org_name,
              orgUuid: result.org_uuid,
              // This check excludes orgs the caller belongs to, so a match
              // here can never be the caller's own.
            })
            return false // block advance
          }
          if (result.exists && result.has_sub_orgs) {
            setDuplicate({
              ...INITIAL_DUPLICATE,
              isDuplicate: true,
              orgName: result.org_name,
              orgUuid: result.org_uuid,
              hasSubOrgs: true,
            })
          }
        } catch {
          // Fail-open: allow advance on error
        }
      }
      if (isCharity) {
        setWizardData({ charityDetails, orgNumber: fieldValue.trim() })
      } else {
        setWizardData((prev) => {
          const base = prev.orgForm ?? EMPTY_ORG_FORM
          return { orgForm: { ...base, orgName: fieldValue.trim() } }
        })
      }

      setCreateError('')
      const freshToken = await getToken()

      if (!orgUuid) {
        // First pass through step 4 — create the org
        try {
          const createdOrg = await addOrg.mutateAsync({
            token: freshToken,
            org: {
              name: isCharity
                ? (charityDetails?.name ?? '')
                : fieldValue.trim(),
              charitiesNumber: isCharity ? fieldValue.trim() : '',
              description: isCharity
                ? (charityDetails?.purposes ?? null)
                : null,
              website: isCharity ? (charityDetails?.website ?? null) : null,
              region_ids: [],
              iwi: [],
              accepts_gambling_proceeds: true,
              accepts_international: false,
            },
            generalInfo: {
              primary_contact_name: user
                ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
                : null,
              primary_contact_phone: null,
              primary_contact_email: user?.email ?? null,
              secondary_contact_name: null,
              secondary_contact_phone: null,
              secondary_contact_email: null,
              postal_address: null,
              physical_address: null,
              website: isCharity ? (charityDetails?.website ?? null) : null,
            },
          })
          setOrgUuid(createdOrg.org_uuid)
          setSearchParams({}, { replace: true })
        } catch (err) {
          if (err instanceof DuplicateOrgNameError) {
            setDuplicate({
              ...INITIAL_DUPLICATE,
              isDuplicate: true,
              orgName: err.duplicate.orgName,
              orgUuid: err.duplicate.orgUuid,
              matchedBy: 'name',
              alreadyMember: err.duplicate.alreadyMember,
            })
          } else {
            setCreateError(
              err instanceof Error
                ? err.message
                : 'Failed to create organisation',
            )
          }
          return false
        }
      } else if (existingOrg) {
        // Returning to step 4 after switching entity type — sync charities fields to DB.
        // Also pre-populate wizardData.orgForm so OrgDetails uses wizard state instead of
        // potentially stale DB cache (which may not reflect our updateOrg call yet).
        setWizardData({
          orgForm: {
            orgName: isCharity
              ? (charityDetails?.name ?? existingOrg.name)
              : fieldValue.trim(),
            website: isCharity
              ? (charityDetails?.website ?? existingOrg.website ?? '')
              : '',
            description: isCharity ? (charityDetails?.purposes ?? '') : '',
            regionIds: existingOrg.region_ids ?? [],
            iwi: existingOrg.iwi ?? [],
            showIwiInput: (existingOrg.iwi?.length ?? 0) > 0,
            refuseGambling: existingOrg.accepts_gambling_proceeds === false,
            acceptInternational: existingOrg.accepts_international ?? false,
          },
        })
        try {
          await updateOrg.mutateAsync({
            token: freshToken,
            editedOrg: {
              ...existingOrg,
              name: isCharity
                ? (charityDetails?.name ?? existingOrg.name)
                : fieldValue.trim(),
              charities_number: isCharity ? fieldValue.trim() : null,
              description: isCharity
                ? (charityDetails?.purposes ?? null)
                : null,
              website: isCharity
                ? (charityDetails?.website ?? existingOrg.website)
                : null,
            },
          })
        } catch (err) {
          if (err instanceof DuplicateOrgNameError) {
            setDuplicate({
              ...INITIAL_DUPLICATE,
              isDuplicate: true,
              orgName: err.duplicate.orgName,
              orgUuid: err.duplicate.orgUuid,
              matchedBy: 'name',
              alreadyMember: err.duplicate.alreadyMember,
            })
          } else {
            setCreateError(
              err instanceof Error
                ? err.message
                : 'Failed to update organisation',
            )
          }
          return false
        }
      }
    },
  })

  /** Run duplicate check after charity/IS validation succeeds */
  const runDuplicateCheck = useCallback(
    async (charityNumber: string) => {
      try {
        const freshToken = await getToken()
        const result = await checkDuplicate.mutateAsync({
          token: freshToken,
          params: { charities_number: charityNumber },
        })
        if (!mountedRef.current) return
        if (result.exists) {
          setDuplicate({
            ...INITIAL_DUPLICATE,
            isDuplicate: true,
            orgName: result.org_name,
            orgUuid: result.org_uuid,
            hasSubOrgs: result.has_sub_orgs ?? false,
            matchedBy: 'charities_number',
          })
        }
      } catch {
        // Fail-open: silently ignore
      }
    },
    [getToken],
  )

  const handleChange = useCallback(
    (value: string) => {
      if (isCharity) {
        const stored = value.toUpperCase()
        setWizardData({ orgNumber: stored, charityDetails: null })
      } else {
        setWizardData((prev) => {
          const base = prev.orgForm ?? EMPTY_ORG_FORM
          return { orgForm: { ...base, orgName: value } }
        })
      }
      setValidationError('')
      setCharityDetails(null)
      setDuplicate(INITIAL_DUPLICATE)
      setGoToOrgError(null)
      setCreateError('')

      if (debounceRef.current) clearTimeout(debounceRef.current)

      // For "other" entities, no debounced validation — check happens on beforeNext
      if (!isCharity) {
        return
      }

      // For charity, validate then check duplicate
      const uppercased = value.toUpperCase()
      if (!uppercased.trim()) {
        setValidating(false)
        return
      }

      setValidating(true)
      debounceRef.current = setTimeout(async () => {
        const trimmed = uppercased.trim()
        const number = trimmed.startsWith('CC') ? trimmed : `CC${trimmed}`

        // DEV: bypass real API for test org — tree-shaken from production
        if (
          import.meta.env.DEV &&
          import.meta.env.VITE_DEV_ONBOARD_TOOLS === 'true' &&
          number.toUpperCase() === 'CC000001A'
        ) {
          const { DEV_TEST_CHARITY_DETAILS } =
            await import('../OnboardingQuickNavData')
          if (!mountedRef.current) return
          setCharityDetails(DEV_TEST_CHARITY_DETAILS as CharityDetails)
          setWizardData({
            charityDetails: DEV_TEST_CHARITY_DETAILS as CharityDetails,
          })
          setValidationError('')
          setValidating(false)
          return
        }

        try {
          const result = await validateCharity.mutateAsync({
            token,
            charityNumber: number,
          })
          if (!mountedRef.current) return
          if (result.success && result.data) {
            setCharityDetails(result.data)
            setWizardData({ charityDetails: result.data })
            setValidationError('')
            // Chain duplicate check after successful validation
            await runDuplicateCheck(number)
            if (!mountedRef.current) return
          } else {
            setCharityDetails(null)
            setValidationError(result.error ?? 'Charity not found')
          }
        } catch {
          if (!mountedRef.current) return
          setCharityDetails(null)
          setValidationError('Failed to validate charity number')
        }
        setValidating(false)
      }, 500)
    },
    [isCharity, token, validateCharity, runDuplicateCheck, setWizardData],
  )

  const handleRequestJoin = async () => {
    if (!duplicate.orgUuid) return
    setRequesting(true)
    try {
      const freshToken = await getToken()
      const result = await requestJoinMutation.mutateAsync({
        token: freshToken,
        orgUuid: duplicate.orgUuid,
      })
      setDuplicate((prev) => ({
        ...prev,
        requestSent: true,
        emailSent: result.email_sent,
      }))
    } catch {
      setDuplicate((prev) => ({ ...prev, requestSent: true, emailSent: false }))
    }
    setRequesting(false)
  }

  const handleGoToOrg = async (targetOrgUuid: string) => {
    if (!user) return
    setSwitchingOrg(true)
    setGoToOrgError(null)
    try {
      const freshToken = await getToken()
      await switchToOrg(freshToken, user, targetOrgUuid)
    } catch {
      setSwitchingOrg(false)
      setGoToOrgError("Couldn't switch organisations. Please try again.")
    }
  }

  const inputId = 'org-number-input'

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Typography variant="h5" sx={{ ...stepHeadingSx, textAlign: 'center' }}>
        {heading}
      </Typography>

      <Typography sx={{ ...stepDescriptionSx, textAlign: 'center' }}>
        {isCharity ? (
          <>
            Enter your charity number (e.g. CC12345). If you don&#39;t know it,
            search for your organisation{' '}
            <a
              href="https://www.charities.govt.nz/charities-in-new-zealand/the-charities-register/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: COLORS.link }}
            >
              in the charities register
            </a>
            .
          </>
        ) : (
          'Enter the name of your organisation.'
        )}
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 480 }}>
        <Typography component="label" htmlFor={inputId} sx={formLabelSx}>
          {heading}
        </Typography>
        <TextField
          id={inputId}
          fullWidth
          disabled={fieldDisabled}
          placeholder={placeholder}
          value={fieldValue}
          onChange={(e) => handleChange(e.target.value)}
          sx={textFieldSx}
          slotProps={{
            input: {
              sx: inputSx,
              endAdornment: validating ? (
                <CircularProgress size={20} sx={{ color: COLORS.primary }} />
              ) : undefined,
            },
          }}
        />

        {validationError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {validationError}
          </Alert>
        )}

        {charityDetails && !validationError && !duplicate.isDuplicate && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Found: <strong>{charityDetails.name}</strong>
            {charityDetails.registration_status && (
              <> — Status: {charityDetails.registration_status}</>
            )}
          </Alert>
        )}

        {createError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {createError}
          </Alert>
        )}

        <DuplicateWarning
          duplicate={duplicate}
          requesting={requesting}
          onRequestJoin={handleRequestJoin}
          onGoToOrg={handleGoToOrg}
          switchingOrg={switchingOrg}
          goToOrgError={goToOrgError}
        />
      </Box>
    </Box>
  )
}
